import { useEffect, useState } from "react";
import {
  Trash2,
  RotateCcw,
  Briefcase,
  MessageSquare,
  Sparkles,
  AlertTriangle,
  X,
} from "lucide-react";
import api from "../lib/api";
import Modal from "./Modal";

const KIND_META = {
  jobcards: { label: "Job cards", icon: Briefcase, color: "#F59E0B" },
  tickets: { label: "Tickets", icon: MessageSquare, color: "#F87171" },
  leads: { label: "Leads", icon: Sparkles, color: "#A78BFA" },
};

/**
 * Customer Bin — soft-deleted job cards / tickets / leads recoverable
 * for 90 days.
 *
 * Manager / admin / owner only. Loaded lazily when the modal opens.
 */
export default function CustomerBinModal({ customerId, open, onClose, onChanged }) {
  const [data, setData] = useState({ jobcards: [], tickets: [], leads: [] });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // {kind, id, action}
  const [confirmPurge, setConfirmPurge] = useState(null);

  const load = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/bin`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId]);

  const restore = async (kind, id) => {
    setBusy({ kind, id, action: "restore" });
    try {
      await api.post(`/${kind}/${id}/restore`);
      await load();
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  const purge = async () => {
    if (!confirmPurge) return;
    const { kind, id } = confirmPurge;
    setBusy({ kind, id, action: "purge" });
    try {
      await api.delete(`/${kind}/${id}/purge`);
      setConfirmPurge(null);
      await load();
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  const total =
    (data.jobcards?.length || 0) + (data.tickets?.length || 0) + (data.leads?.length || 0);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Recently deleted"
        testId="customer-bin-modal"
        maxWidth="max-w-2xl"
      >
        <div className="space-y-4">
          <div className="text-xs text-[#a0aabf] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <span>
              Items deleted from this customer are kept here for{" "}
              <span className="text-white font-bold">90 days</span> before they're
              purged automatically. Restore brings them back where they were;
              purge removes them for good.
            </span>
          </div>

          {loading ? (
            <div className="text-sm text-[#647087] py-8 text-center">Loading…</div>
          ) : total === 0 ? (
            <div className="text-sm text-[#647087] py-8 text-center" data-testid="bin-empty">
              The bin is empty. Nothing to restore.
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(data).map(([kind, rows]) => {
                if (!rows.length) return null;
                const meta = KIND_META[kind];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <section key={kind} data-testid={`bin-section-${kind}`}>
                    <div
                      className="flex items-center gap-2 mb-2"
                      style={{ color: meta.color }}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-xs uppercase tracking-[0.18em] font-bold">
                        {meta.label} ({rows.length})
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {rows.map((r) => {
                        const title =
                          r.title || r.subject || r.short_id || r.id?.slice(0, 8);
                        const days = r.days_until_purge ?? 90;
                        const danger = days <= 7;
                        return (
                          <li
                            key={r.id}
                            className="flex items-center gap-2 border border-white/5 rounded px-3 py-2 hover:bg-white/[0.02] flex-wrap"
                            data-testid={`bin-row-${kind}-${r.id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">
                                {title}
                              </div>
                              <div className="text-[10px] text-[#647087]">
                                Deleted {r.deleted_at?.slice(0, 10)} by{" "}
                                {r.deleted_by_name || "—"} ·{" "}
                                <span
                                  className={
                                    danger ? "text-rose-300 font-bold" : "text-[#647087]"
                                  }
                                >
                                  {days} day{days === 1 ? "" : "s"} until purge
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => restore(kind, r.id)}
                              disabled={!!busy}
                              className="hv-btn hv-btn-ghost text-xs text-emerald-300"
                              data-testid={`bin-restore-${r.id}`}
                            >
                              <RotateCcw className="w-3 h-3" /> Restore
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmPurge({ kind, id: r.id, title })}
                              disabled={!!busy}
                              className="hv-btn hv-btn-ghost text-xs text-rose-300"
                              data-testid={`bin-purge-${r.id}`}
                              title="Permanently delete"
                            >
                              <X className="w-3 h-3" /> Purge
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      {confirmPurge && (
        <Modal
          open
          onClose={() => setConfirmPurge(null)}
          title="Purge permanently?"
          testId="bin-confirm-purge"
          maxWidth="max-w-sm"
        >
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Trash2 className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <p className="text-sm text-[#a0aabf]">
                This will permanently remove{" "}
                <span className="text-white font-bold">{confirmPurge.title}</span>
                . This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setConfirmPurge(null)}
                className="hv-btn hv-btn-ghost flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={purge}
                className="hv-btn hv-btn-primary flex-1 !bg-rose-500 !border-rose-500 hover:!bg-rose-400"
                data-testid="bin-confirm-purge-yes"
              >
                Purge
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
